import { Warning } from "@procore/core-icons";
import { Box, Button, Modal, Typography } from "@procore/core-react";
import { useId } from "react";
import { useTranslation } from "react-i18next";

import { getBrowserDisplayName } from "../browserDisplayName";
import { trapFocusWithoutDismiss, usePreserveBodyLayoutWhileModalOpen } from "./guidanceModalLock";

type IntroPromptProps = {
    onGetStarted?: () => void;
    isWindows?: boolean;
    accelerationOff?: boolean;
    testId?: string;
    preserveBodyLayout?: boolean;
};

/** Figma: BIM — Windows GPU Wizard, intro card 442×330. */
const INTRO_DIALOG_WIDTH = 442;
const INTRO_DIALOG_HEIGHT = 330;
/** Figma warning glyph fill; not `colors.yellow40` (#B28500). */
const WARNING_ICON_COLOR = "#BD910F";

export function IntroPrompt({
    onGetStarted,
    isWindows = true,
    accelerationOff = false,
    testId = "gpu-guidance-intro",
    preserveBodyLayout = false,
}: IntroPromptProps) {
    const { t } = useTranslation();
    const titleId = useId();
    const descriptionId = useId();
    const browser = getBrowserDisplayName();
    const useMacAccelerationCopy = accelerationOff && !isWindows;

    usePreserveBodyLayoutWhileModalOpen(preserveBodyLayout);

    return (
        <Modal
            open
            role="dialog"
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
            howToClose={[]}
            onClose={trapFocusWithoutDismiss}
            style={{
                width: INTRO_DIALOG_WIDTH,
                maxWidth: INTRO_DIALOG_WIDTH,
                height: INTRO_DIALOG_HEIGHT,
                borderRadius: 8,
                boxShadow: "0 0 28px rgba(0, 0, 0, 0.28)",
                overflow: "hidden",
            }}
        >
            <Modal.Body
                noSideSpacing
                data-testid={testId}
                style={{
                    padding: 0,
                    maxWidth: "none",
                    overflow: "hidden",
                    height: INTRO_DIALOG_HEIGHT,
                    outline: "none",
                    border: "none",
                    boxShadow: "none",
                }}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    textAlign="center"
                    style={{
                        boxSizing: "border-box",
                        width: "100%",
                        height: INTRO_DIALOG_HEIGHT,
                        padding: "63px 36px 40px",
                    }}
                >
                    <Warning size="lg" aria-hidden="true" style={{ color: WARNING_ICON_COLOR }} />
                    <Typography as="h2" id={titleId} intent="h3" weight="bold" color="black" style={{ marginTop: 28 }}>
                        {useMacAccelerationCopy ? t("gpuGuidance.hardStop.mac.title") : t("gpuGuidance.intro.title")}
                    </Typography>
                    {useMacAccelerationCopy ? (
                        <Typography id={descriptionId} intent="body" color="gray40" style={{ marginTop: 16 }}>
                            {t("gpuGuidance.hardStop.mac.bodyBefore")}{" "}
                            <Typography as="span" intent="body" weight="semibold" color="gray40">
                                {t("gpuGuidance.hardStop.mac.emphasis")}
                            </Typography>{" "}
                            {t("gpuGuidance.hardStop.mac.bodyAfter", { browser })}
                        </Typography>
                    ) : (
                        <Typography id={descriptionId} intent="body" color="gray40" style={{ marginTop: 16 }}>
                            {t("gpuGuidance.intro.body")}
                        </Typography>
                    )}
                    <Box style={{ marginTop: "auto" }}>
                        {useMacAccelerationCopy ? (
                            <span role="note" data-testid="gpu-guidance-mac-action">
                                <Typography intent="body" weight="semibold" color="gray40">
                                    {t("gpuGuidance.hardStop.mac.action", { browser })}
                                </Typography>
                            </span>
                        ) : (
                            <Button variant="primary" autoFocus onClick={onGetStarted}>
                                {t("gpuGuidance.getStarted")}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Modal.Body>
        </Modal>
    );
}
